export const BOOKING_AUDIENCE_LABELS = {
    individual: 'Individual',
    couples: 'Couples',
};
/** How far ahead therapists (and clients) can plan availability — 3 months */
export const AVAILABILITY_HORIZON_MONTHS = 3;
export const TIME_OPTIONS = (() => {
    const options = [];
    for (let h = 6; h <= 22; h++) {
        options.push(`${String(h).padStart(2, '0')}:00`);
        options.push(`${String(h).padStart(2, '0')}:30`);
    }
    return options;
})();
export function formatTime12h(time24) {
    const [hStr, mStr] = time24.split(':');
    const h = parseInt(hStr ?? '0', 10);
    const m = parseInt(mStr ?? '0', 10);
    const ampm = h >= 12 ? 'pm' : 'am';
    const hour = h % 12 || 12;
    if (m === 0)
        return `${hour}${ampm}`;
    return `${hour}:${String(m).padStart(2, '0')}${ampm}`;
}
export function formatTimeRange12h(startTime, endTime) {
    return `${formatTime12h(startTime)} - ${formatTime12h(endTime)}`;
}
export function formatTimeRanges12h(ranges) {
    if (ranges.length === 0)
        return 'Not available';
    return ranges.map((r) => formatTimeRange12h(r.startTime, r.endTime)).join(', ');
}
export function formatResponseTime(hours) {
    if (hours <= 0)
        return '~ 1 hr';
    if (hours < 1)
        return '~ 30 min';
    if (hours === 1)
        return '~ 1 hr';
    return `~ ${hours} hrs`;
}
export function isoDateLocal(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
export function parseLocalDate(dateStr) {
    return new Date(`${dateStr}T12:00:00`);
}
export function addDaysLocal(dateStr, days) {
    const d = parseLocalDate(dateStr);
    d.setDate(d.getDate() + days);
    return isoDateLocal(d);
}
export function availabilityHorizonEnd(from = new Date()) {
    const end = new Date(from.getFullYear(), from.getMonth() + AVAILABILITY_HORIZON_MONTHS, from.getDate());
    return end;
}
export function isWithinAvailabilityHorizon(dateStr, from = new Date()) {
    const d = parseLocalDate(dateStr);
    const today = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = availabilityHorizonEnd(from);
    return d >= today && d <= end;
}
export function getWeeklyRangesForDate(slots, date) {
    const dayOfWeek = date.getDay();
    return slots.filter((s) => s.dayOfWeek === dayOfWeek && s.isAvailable !== false);
}
export function getAvailabilityForDate(slots, date) {
    const [first] = getWeeklyRangesForDate(slots, date);
    return first ?? null;
}
export function hasWeeklyAvailabilityOnDate(slots, date) {
    return getWeeklyRangesForDate(slots, date).length > 0;
}
export function weekStrip(startDate, days = 7) {
    const result = [];
    for (let i = 0; i < days; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        result.push(d);
    }
    return result;
}
export function monthGrid(year, month) {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const cells = [];
    for (let i = 0; i < first.getDay(); i++)
        cells.push(null);
    for (let day = 1; day <= last.getDate(); day++) {
        cells.push(new Date(year, month, day));
    }
    while (cells.length % 7 !== 0)
        cells.push(null);
    return cells;
}
export const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DAY_NAMES_LONG = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
export const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
export function isSameDay(a, b) {
    return (a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate());
}
export function isToday(date) {
    return isSameDay(date, new Date());
}
/**
 * Appointment times are a Lagos wall clock stored in UTC fields.
 * 17:00Z means 5:00pm in Nigeria, which is 16:00 UTC.
 */
const LAGOS_OFFSET_MS = 60 * 60 * 1000;
export function sessionWallClockMs(iso) {
    const date = typeof iso === 'string' ? new Date(iso) : iso;
    const wall = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());
    if (Number.isNaN(wall))
        return Number.NaN;
    return wall - LAGOS_OFFSET_MS;
}
/** True when a bookable slot instant is already in the past. */
export function isPastSessionSlot(iso, now = new Date()) {
    const time = sessionWallClockMs(iso);
    return Number.isNaN(time) || time <= now.getTime();
}
/** Calendar day stored on the appointment, without shifting it into a local timezone. */
export function sessionWallDateKey(iso) {
    const date = typeof iso === 'string' ? new Date(iso) : iso;
    if (Number.isNaN(date.getTime()))
        return '';
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${date.getUTCFullYear()}-${month}-${day}`;
}
/** True when the appointment's stored date is the calendar day showing on this device. */
export function isSessionWallToday(iso, now = new Date()) {
    const key = sessionWallDateKey(iso);
    return key !== '' && key === isoDateLocal(now);
}
/**
 * Current time on the same clock stored in scheduledAt.
 * Use this for database comparisons. Do not add an hour to Date.now() at the call site.
 */
export function appClockNow(now = new Date()) {
    return new Date(now.getTime() + LAGOS_OFFSET_MS);
}
/** Appointment time as shown in the app, for emails, receipts, and lists. */
export function formatSessionStamp(iso, locales = 'en-NG') {
    return formatSessionWallTime(iso, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }, locales);
}
/**
 * Appointment times are stored as a wall clock in UTC (18:00Z means 6:00pm on the picker).
 * Format with this so a phone in Lagos does not add another hour.
 */
export function formatSessionWallTime(iso, options, locales = 'en-US') {
    const date = typeof iso === 'string' ? new Date(iso) : iso;
    return date.toLocaleString(locales, { ...options, timeZone: 'UTC' });
}
export function startOfWeekSunday(date) {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay());
    return isoDateLocal(d);
}
export function defaultTimeRange() {
    return { startTime: '09:00', endTime: '17:00' };
}
export function validateTimeRange(startTime, endTime) {
    const [sh, sm] = startTime.split(':').map((v) => parseInt(v, 10));
    const [eh, em] = endTime.split(':').map((v) => parseInt(v, 10));
    const startMin = (sh ?? 0) * 60 + (sm ?? 0);
    const endMin = (eh ?? 0) * 60 + (em ?? 0);
    return endMin > startMin;
}
//# sourceMappingURL=availability.js.map